(() => {
  let uiController;
  let maintenanceObserver;
  let popupFrame = null;

  const normalizeText = (value = '') => value.replace(/\s+/g, ' ').trim();

  const isConstructionTitle = (text) => {
    const value = normalizeText(text).toLowerCase();
    return value === 'website under construction' || value === 'site web en construction' || value === 'site en construction';
  };

  const findConstructionCard = (titleNode, container) => {
    let node = titleNode;
    while (node && node !== container) {
      const text = normalizeText(node.textContent);
      const interactive = node.querySelector?.('a, button');
      if (interactive && text.length < 1200) return node;
      node = node.parentElement;
    }
    return titleNode.parentElement;
  };

  const dedupeConstructionNotices = () => {
    const productSection = document.querySelector('.product-section');
    if (!productSection) return false;

    const titles = Array.from(productSection.querySelectorAll('*')).filter((node) => {
      if (node.children.length !== 0) return false;
      return isConstructionTitle(node.textContent);
    });

    if (titles.length < 2) return false;

    const cards = Array.from(
      new Set(
        titles
          .map((title) => findConstructionCard(title, productSection))
          .filter((card) => card && card !== productSection)
      )
    );

    if (cards.length >= 2) {
      cards.slice(0, -1).forEach((card) => card.remove());
      return true;
    }

    const firstCard = findConstructionCard(titles[0], productSection);
    if (firstCard && firstCard !== productSection) {
      firstCard.remove();
      return true;
    }

    return false;
  };

  const observeConstructionNotices = () => {
    maintenanceObserver?.disconnect();

    if (dedupeConstructionNotices()) return;

    const productSection = document.querySelector('.product-section');
    if (!productSection) return;

    maintenanceObserver = new MutationObserver(() => {
      if (dedupeConstructionNotices()) maintenanceObserver?.disconnect();
    });

    maintenanceObserver.observe(productSection, { childList: true, subtree: true });
    window.setTimeout(() => maintenanceObserver?.disconnect(), 12000);
  };

  const initProductAccordion = (signal) => {
    document.querySelectorAll('.about__accordion-toggle').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const description = toggle.nextElementSibling?.classList.contains('about__accordion-description') ? toggle.nextElementSibling : toggle.parentElement?.querySelector('.about__accordion-description');
        if (!description) return;
        const isOpen = toggle.classList.toggle('active');
        description.hidden = !isOpen;
      }, { signal });
    });

    document.querySelectorAll('.product__accordion details').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        document.querySelectorAll('.product__accordion details[open]').forEach((other) => {
          if (other !== details) other.removeAttribute('open');
        });
      }, { signal });
    });
  };

  const initCustomCursor = (signal) => {
    const zoomTriggers = document.querySelectorAll('.product__media-toggle, .product-media-modal__content');
    if (!zoomTriggers.length) return;

    let cursor = document.querySelector('.product-zoom-cursor');
    if (!cursor) {
      cursor = document.createElement('div');
      cursor.className = 'product-zoom-cursor';
      document.body.appendChild(cursor);
    }
    cursor.classList.remove('active');

    const moveCursor = (event) => {
      cursor.style.transform = `translate3d(${event.clientX - 24}px, ${event.clientY - 24}px, 0)`;
    };

    zoomTriggers.forEach((trigger) => {
      trigger.addEventListener('mouseenter', () => {
        cursor.classList.add('active');
        document.addEventListener('mousemove', moveCursor, { signal, passive: true });
      }, { signal });
      trigger.addEventListener('mouseleave', () => cursor.classList.remove('active'), { signal });
    });
  };

  const initZoomImage = (signal) => {
    const modal = document.querySelector('.product-media-modal__content');
    if (!modal) return;
    modal.querySelectorAll('.js-image-zoom').forEach((image) => {
      image.addEventListener('click', () => modal.classList.toggle('zoom'), { signal });
    });
  };

  const updatePopupVisibility = () => {
    popupFrame = null;
    const stickyBar = document.querySelector('.product__popup');
    const secondSection = document.querySelector('.product-section + section');
    const footer = document.querySelector('footer');
    if (!stickyBar || !secondSection || !footer) return;

    const secondSectionTop = secondSection.getBoundingClientRect().top + window.scrollY;
    const footerTop = footer.getBoundingClientRect().top + window.scrollY;
    const viewportBottom = window.scrollY + window.innerHeight;
    stickyBar.classList.toggle('active', window.scrollY > secondSectionTop && viewportBottom < footerTop);
  };

  const schedulePopupUpdate = () => {
    if (popupFrame !== null) return;
    popupFrame = window.requestAnimationFrame(updatePopupVisibility);
  };

  const initProductUI = () => {
    uiController?.abort();
    uiController = new AbortController();
    const { signal } = uiController;

    initProductAccordion(signal);
    initZoomImage(signal);
    initCustomCursor(signal);
    observeConstructionNotices();
    schedulePopupUpdate();

    window.addEventListener('scroll', schedulePopupUpdate, { signal, passive: true });
    window.addEventListener('resize', schedulePopupUpdate, { signal, passive: true });
  };

  window.reinitProductUI = initProductUI;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductUI, { once: true });
  } else {
    initProductUI();
  }
  document.addEventListener('shopify:section:load', initProductUI);

  if (!customElements.get('floated-form')) {
    customElements.define('floated-form', class FloatedForm extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';
        this.renderForm();
      }

      async renderForm() {
        const productUrl = this.getAttribute('data-product-url');
        if (!productUrl) return;

        try {
          const response = await fetch(productUrl);
          if (!response.ok) throw new Error(`Unable to load floated product form (${response.status})`);

          const responseText = await response.text();
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('div[id^="ProductInfo-"]');
          if (!this.productElement) return;

          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.setInnerHTML(this, this.productElement.innerHTML);

          if (window.Shopify?.PaymentButton) window.Shopify.PaymentButton.init();
          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.initDropdowns();
          this.initShowMoreFields();
          window.reinitProductUI?.();
        } catch (error) {
          console.error('Floated product form:', error);
        }
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;
        element.querySelectorAll('script').forEach((oldScript) => {
          const newScript = document.createElement('script');
          Array.from(oldScript.attributes).forEach((attribute) => newScript.setAttribute(attribute.name, attribute.value));
          newScript.textContent = oldScript.textContent;
          oldScript.replaceWith(newScript);
        });
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement?.dataset.section;
        if (!sectionId) return;
        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `floated-${sectionId}`);
        this.productElement.querySelectorAll('variant-selects, variant-radios').forEach((picker) => {
          picker.dataset.originalSection = sectionId;
        });
      }

      removeDOMElements() {
        if (!this.productElement) return;
        const selectors = [
          '.product__text', '.product__tags', '.product__title__wrapper', '.product__custom-badges',
          '.price-wrapper', '.product__description', '.product__sku', '.product__inventory',
          'pickup-availability', '.custom-liquid', 'product-recommendations', '.share-buttons',
          '.product-popup', 'floated-form', '[data-shopify-app-block]'
        ];
        selectors.forEach((selector) => {
          this.productElement.querySelectorAll(selector).forEach((element) => element.remove());
        });
      }

      initShowMoreFields() {
        this.querySelectorAll('.js-show-more').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            const parent = button.parentElement;
            parent?.querySelectorAll('input.hidden, label.hidden').forEach((element) => element.classList.remove('hidden'));
            button.remove();
          });
        });
      }

      initDropdowns() {
        this.querySelectorAll('.product-form__controls--dropdown').forEach((control) => {
          const list = control.querySelector('.dropdown-select ul');
          const label = control.querySelector('.dropdown-select .select-label');
          if (!list || !label) return;

          label.addEventListener('click', (event) => {
            event.stopPropagation();
            list.classList.toggle('active');
          });

          list.querySelectorAll('li').forEach((item) => {
            item.addEventListener('click', () => {
              const labelText = label.querySelector('span');
              if (labelText) {
                labelText.textContent = item.textContent;
                labelText.setAttribute('title', item.textContent.trim());
              }
              list.classList.remove('active');
            });
          });

          document.addEventListener('click', () => list.classList.remove('active'));
        });
      }
    });
  }
})();
